import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownSelect } from "../components/shared/DropdownHooks";
import { Shield, Plus, AlertTriangle, CheckCircle2, Clock, Lock, Search, FileText, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

export default function CompliancePage({ onLogout, userRole, isFeatureEnabled }) {
  const [tab, setTab] = useState("ncr");
  const tabs = [
    { key: "ncr", label: "NCR Reports" },
    { key: "loto", label: "LOTO Procedures" },
    { key: "qualifications", label: "Training & Quals" },
    { key: "signatures", label: "Digital Signatures" },
    { key: "tpi", label: "Two-Person Integrity" },
    { key: "prints", label: "Print Tracking" },
  ];

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="compliance-page">
        <PageHeader title="Compliance & Safety" subtitle="NCR tracking, LOTO procedures, training qualifications, and digital signatures" testId="compliance-title" />

        <div className="flex border-b-2 border-[#E2E8F0]" data-testid="compliance-tabs">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-6 py-3 text-sm font-bold tracking-wide relative ${tab === t.key ? "text-[#DC2626]" : "text-[#78716C] hover:text-[#1C1917]"}`} data-testid={`tab-${t.key}`}>
              {t.label}
              {tab === t.key && <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#DC2626]" />}
            </button>
          ))}
        </div>

        {tab === "ncr" && <NCRPanel userRole={userRole} />}
        {tab === "loto" && <LOTOPanel userRole={userRole} />}
        {tab === "qualifications" && <QualificationsPanel userRole={userRole} />}
        {tab === "signatures" && <SignaturesPanel />}
        {tab === "tpi" && <TPIPanel userRole={userRole} />}
        {tab === "prints" && <PrintLogPanel userRole={userRole} />}
      </div>
    </Layout>
  );
}

// ─── NCR Panel ───────────────────────────────────────────────────────

function NCRPanel({ userRole }) {
  const [ncrs, setNcrs] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", machine_number: "", severity: "medium", category: "" });
  const [search, setSearch] = useState("");
  const isAdmin = userRole === "admin" || userRole === "superintendent";

  const fetch = useCallback(async () => {
    const params = search ? `?search=${encodeURIComponent(search)}` : "";
    const [ncrR, statR] = await Promise.all([
      axios.get(`${API}/ncr${params}`, { headers: getAuthHeaders() }),
      axios.get(`${API}/ncr/stats`, { headers: getAuthHeaders() }),
    ]);
    setNcrs(ncrR.data); setStats(statR.data);
  }, [search]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/ncr`, form, { headers: getAuthHeaders() });
      toast.success("NCR created"); setShowForm(false); setForm({ title: "", description: "", machine_number: "", severity: "medium", category: "" }); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const sevColor = { critical: "#DC2626", high: "#EA580C", medium: "#D97706", low: "#10B981" };
  const statColor = { open: "#DC2626", investigating: "#D97706", resolved: "#2563EB", closed: "#10B981" };

  return (
    <div className="space-y-4" data-testid="ncr-panel">
      {stats && (
        <div className="grid grid-cols-5 gap-3">
          {[{ k: "open", l: "Open" }, { k: "investigating", l: "Investigating" }, { k: "resolved", l: "Resolved" }, { k: "closed", l: "Closed" }].map(s => (
            <div key={s.k} className="bg-white border border-[#E2E8F0] p-4 text-center">
              <div className="text-2xl font-mono font-black" style={{ color: statColor[s.k] || "#64748B" }}>{stats.by_status?.[s.k] || 0}</div>
              <div className="text-xs text-[#64748B] uppercase font-bold mt-1">{s.l}</div>
            </div>
          ))}
          <div className="bg-[#0F172A] p-4 text-center"><div className="text-2xl font-mono font-black text-white">{stats.total || 0}</div><div className="text-xs text-[#94A3B8] uppercase font-bold mt-1">Total</div></div>
        </div>
      )}
      <div className="flex gap-3">
        <div className="relative flex-1"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search NCRs..." className="pl-10 rounded-none" data-testid="ncr-search" /></div>
        <Button onClick={() => setShowForm(true)} className="bg-[#DC2626] hover:bg-[#991B1B] text-white rounded-none" data-testid="create-ncr-btn"><Plus size={14} className="mr-1" />New NCR</Button>
      </div>
      <div className="bg-white border border-[#E2E8F0]">
        <table className="w-full text-sm" data-testid="ncr-table">
          <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
            <tr>{["NCR #", "Title", "Machine", "Severity", "Status", "Date"].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-bold uppercase">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#F1F5F9]">
            {ncrs.map(n => (
              <tr key={n.id} className="hover:bg-[#FAFAFA]" data-testid={`ncr-row-${n.ncr_number}`}>
                <td className="px-4 py-3 font-mono font-bold text-[#DC2626]">{n.ncr_number}</td>
                <td className="px-4 py-3 text-[#0F172A] font-medium">{n.title}</td>
                <td className="px-4 py-3 font-mono text-[#64748B]">{n.machine_number || "—"}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 text-[10px] font-bold uppercase" style={{ color: sevColor[n.severity], backgroundColor: `${sevColor[n.severity]}10` }}>{n.severity}</span></td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 text-[10px] font-bold uppercase" style={{ color: statColor[n.status], backgroundColor: `${statColor[n.status]}10` }}>{n.status}</span></td>
                <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{new Date(n.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {ncrs.length === 0 && <div className="p-8 text-center text-[#94A3B8]"><CheckCircle2 size={32} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No NCRs found</p></div>}
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0]" aria-describedby="ncr-form-desc">
          <DialogHeader><DialogTitle className="font-bold text-[#0F172A]">New Non-Conformance Report</DialogTitle></DialogHeader>
          <div id="ncr-form-desc" className="sr-only">Create a new NCR</div>
          <form onSubmit={handleCreate} className="space-y-4">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="mt-1 rounded-none" data-testid="ncr-title-input" /></div>
            <div><Label>Description</Label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1 w-full h-20 px-3 py-2 border border-[#E2E8F0] text-sm" data-testid="ncr-desc-input" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Machine #</Label><Input value={form.machine_number} onChange={e => setForm({ ...form, machine_number: e.target.value })} className="mt-1 rounded-none font-mono" /></div>
              <div><Label>Severity</Label><DropdownSelect dropdownKey="ncr_severities" value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })} className="mt-1 w-full h-10 px-3 border border-[#E2E8F0] bg-white text-sm" testId="ncr-severity" /></div>
              <div><Label>Category</Label><select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="mt-1 w-full h-10 px-3 border border-[#E2E8F0] bg-white text-sm">{["", "material", "process", "equipment", "documentation"].map(c => <option key={c} value={c}>{c || "Select..."}</option>)}</select></div>
            </div>
            <Button type="submit" className="w-full bg-[#DC2626] hover:bg-[#991B1B] text-white rounded-none" data-testid="submit-ncr-btn">CREATE NCR</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── LOTO Panel ──────────────────────────────────────────────────────

function LOTOPanel({ userRole }) {
  const [procedures, setProcedures] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ machine_number: "", title: "", hazards: "", required_ppe: "", steps: "" });
  const isAdmin = userRole === "admin" || userRole === "superintendent";

  useEffect(() => { axios.get(`${API}/loto/procedures`, { headers: getAuthHeaders() }).then(r => setProcedures(r.data)).catch(() => {}); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, hazards: form.hazards.split(",").map(s => s.trim()).filter(Boolean), required_ppe: form.required_ppe.split(",").map(s => s.trim()).filter(Boolean), steps: form.steps.split("\n").filter(Boolean).map((s, i) => ({ step_number: i + 1, instruction: s.trim() })) };
      await axios.post(`${API}/loto/procedures`, payload, { headers: getAuthHeaders() });
      toast.success("LOTO procedure created"); setShowForm(false); setForm({ machine_number: "", title: "", hazards: "", required_ppe: "", steps: "" });
      const r = await axios.get(`${API}/loto/procedures`, { headers: getAuthHeaders() }); setProcedures(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleAcknowledge = async (procId) => {
    try {
      await axios.post(`${API}/loto/acknowledge`, { procedure_id: procId }, { headers: getAuthHeaders() });
      toast.success("LOTO procedure acknowledged");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="loto-panel">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#64748B]">{procedures.length} procedure(s) on file</p>
        {isAdmin && <Button onClick={() => setShowForm(true)} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="create-loto-btn"><Plus size={14} className="mr-1" />New Procedure</Button>}
      </div>
      <div className="space-y-3">
        {procedures.map(p => (
          <div key={p.id} className="bg-white border border-[#E2E8F0] p-5" data-testid={`loto-${p.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Lock size={18} className="text-[#D97706]" />
                <h3 className="font-bold text-[#0F172A]">{p.title}</h3>
                <span className="font-mono text-xs text-[#64748B]">{p.machine_number}</span>
              </div>
              <Button size="sm" onClick={() => handleAcknowledge(p.id)} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none text-xs" data-testid={`ack-loto-${p.id}`}><CheckCircle2 size={12} className="mr-1" />Acknowledge</Button>
            </div>
            {p.hazards?.length > 0 && <div className="mb-2"><span className="text-xs font-bold text-[#DC2626] uppercase">Hazards: </span><span className="text-xs text-[#64748B]">{p.hazards.join(", ")}</span></div>}
            {p.required_ppe?.length > 0 && <div className="mb-2"><span className="text-xs font-bold text-[#2563EB] uppercase">PPE: </span><span className="text-xs text-[#64748B]">{p.required_ppe.join(", ")}</span></div>}
            {p.steps?.length > 0 && (
              <div className="mt-3 space-y-1">
                {p.steps.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="w-6 h-6 bg-[#F1F5F9] flex items-center justify-center text-xs font-bold text-[#64748B] shrink-0">{s.step_number || i + 1}</span>
                    <span className="text-[#0F172A]">{s.instruction}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {procedures.length === 0 && <div className="p-8 text-center text-[#94A3B8]"><Lock size={32} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No LOTO procedures defined</p></div>}
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0]" aria-describedby="loto-form-desc">
          <DialogHeader><DialogTitle className="font-bold text-[#0F172A]">New LOTO Procedure</DialogTitle></DialogHeader>
          <div id="loto-form-desc" className="sr-only">Create a lockout tagout procedure</div>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Machine # *</Label><Input value={form.machine_number} onChange={e => setForm({ ...form, machine_number: e.target.value })} required className="mt-1 rounded-none font-mono" /></div>
              <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="mt-1 rounded-none" /></div>
            </div>
            <div><Label>Hazards (comma-separated)</Label><Input value={form.hazards} onChange={e => setForm({ ...form, hazards: e.target.value })} className="mt-1 rounded-none" placeholder="Electrical, Hydraulic, Pneumatic" /></div>
            <div><Label>Required PPE (comma-separated)</Label><Input value={form.required_ppe} onChange={e => setForm({ ...form, required_ppe: e.target.value })} className="mt-1 rounded-none" placeholder="Safety glasses, Gloves, Hard hat" /></div>
            <div><Label>Steps (one per line)</Label><textarea value={form.steps} onChange={e => setForm({ ...form, steps: e.target.value })} className="mt-1 w-full h-28 px-3 py-2 border border-[#E2E8F0] text-sm font-mono" placeholder={"Notify all affected personnel\nShut down equipment\nDisconnect energy sources\nApply lockout devices\nVerify zero energy state"} /></div>
            <Button type="submit" className="w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="submit-loto-btn">CREATE PROCEDURE</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Qualifications Panel ────────────────────────────────────────────

function QualificationsPanel({ userRole }) {
  const [quals, setQuals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ user_id: "", machine_number: "", qualification_type: "maintenance", training_date: "", expires_at: "", notes: "" });
  const isAdmin = userRole === "admin" || userRole === "superintendent";

  useEffect(() => { axios.get(`${API}/qualifications`, { headers: getAuthHeaders() }).then(r => setQuals(r.data)).catch(() => {}); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/qualifications`, form, { headers: getAuthHeaders() });
      toast.success("Qualification added"); setShowForm(false);
      const r = await axios.get(`${API}/qualifications`, { headers: getAuthHeaders() }); setQuals(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this qualification?")) return;
    try {
      await axios.delete(`${API}/qualifications/${id}`, { headers: getAuthHeaders() });
      toast.success("Qualification revoked"); setQuals(quals.filter(q => q.id !== id));
    } catch { toast.error("Revoke failed"); }
  };

  const typeColors = { operator: "#2563EB", maintenance: "#10B981", inspection: "#D97706", supervisor: "#8B5CF6" };
  const isExpired = (d) => { try { return new Date(d) < new Date(); } catch { return false; } };

  return (
    <div className="space-y-4" data-testid="qualifications-panel">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#64748B]">{quals.filter(q => q.status === "active").length} active qualification(s)</p>
        {isAdmin && <Button onClick={() => setShowForm(true)} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="create-qual-btn"><Plus size={14} className="mr-1" />Add Qualification</Button>}
      </div>
      <div className="bg-white border border-[#E2E8F0]">
        <table className="w-full text-sm" data-testid="quals-table">
          <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
            <tr>{["User", "Machine", "Type", "Trained", "Expires", "Status", ""].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-bold uppercase">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#F1F5F9]">
            {quals.map(q => {
              const expired = q.expires_at && isExpired(q.expires_at);
              return (
                <tr key={q.id} className={`hover:bg-[#FAFAFA] ${expired ? "bg-[#FEF2F2]" : ""}`}>
                  <td className="px-4 py-3 font-mono text-[#0F172A]">{q.user_id}</td>
                  <td className="px-4 py-3 font-mono text-[#64748B]">{q.machine_number || "All"}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 text-[10px] font-bold uppercase" style={{ color: typeColors[q.qualification_type] || "#64748B", backgroundColor: `${typeColors[q.qualification_type] || "#64748B"}10` }}>{q.qualification_type}</span></td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">{q.training_date || "—"}</td>
                  <td className="px-4 py-3 text-xs font-bold" style={{ color: expired ? "#DC2626" : "#64748B" }}>{q.expires_at ? new Date(q.expires_at).toLocaleDateString() : "Never"}{expired && " ⚠"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${q.status === "active" ? "bg-[#F0FDF4] text-[#15803D]" : "bg-[#FEF2F2] text-[#DC2626]"}`}>{q.status}</span></td>
                  <td className="px-4 py-3">{isAdmin && q.status === "active" && <button onClick={() => handleRevoke(q.id)} className="text-[#DC2626] hover:text-[#991B1B]"><Trash2 size={14} /></button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {quals.length === 0 && <div className="p-8 text-center text-[#94A3B8]"><Users size={32} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No qualifications on record</p></div>}
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0]" aria-describedby="qual-form-desc">
          <DialogHeader><DialogTitle className="font-bold text-[#0F172A]">Add Qualification</DialogTitle></DialogHeader>
          <div id="qual-form-desc" className="sr-only">Add a training qualification</div>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Employee ID *</Label><Input value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })} required className="mt-1 rounded-none font-mono" /></div>
              <div><Label>Machine #</Label><Input value={form.machine_number} onChange={e => setForm({ ...form, machine_number: e.target.value })} className="mt-1 rounded-none font-mono" placeholder="Leave blank for all" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Type *</Label><select value={form.qualification_type} onChange={e => setForm({ ...form, qualification_type: e.target.value })} className="mt-1 w-full h-10 px-3 border border-[#E2E8F0] bg-white text-sm">{["operator", "maintenance", "inspection", "supervisor"].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}</select></div>
              <div><Label>Training Date</Label><Input type="date" value={form.training_date} onChange={e => setForm({ ...form, training_date: e.target.value })} className="mt-1 rounded-none" /></div>
              <div><Label>Expires</Label><Input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} className="mt-1 rounded-none" /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-none" /></div>
            <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="submit-qual-btn">ADD QUALIFICATION</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Signatures Panel ────────────────────────────────────────────────

function SignaturesPanel() {
  const [signatures, setSignatures] = useState([]);
  useEffect(() => {
    axios.get(`${API}/signatures/blueprint/all`, { headers: getAuthHeaders() }).catch(() => {});
    // Load recent signatures from audit log
    axios.get(`${API}/admin/audit-log?action=digital_signature&limit=50`, { headers: getAuthHeaders() })
      .then(r => setSignatures(r.data.entries || []))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-4" data-testid="signatures-panel">
      <p className="text-sm text-[#64748B]">Digital signatures are automatically created when approving work orders, releasing blueprints, or acknowledging LOTO procedures. Each signature includes a SHA-256 hash for integrity verification.</p>
      <div className="bg-white border border-[#E2E8F0]">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
            <tr>{["Timestamp", "User", "Action", "Target"].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-bold uppercase">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#F1F5F9]">
            {signatures.map(s => (
              <tr key={s.id} className="hover:bg-[#FAFAFA]">
                <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{new Date(s.timestamp).toLocaleString()}</td>
                <td className="px-4 py-3 text-[#0F172A] font-medium">{s.user_id}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-[#EFF6FF] text-[#2563EB]">signed</span></td>
                <td className="px-4 py-3 text-xs text-[#64748B]">{JSON.stringify(s.details)?.slice(0, 80)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {signatures.length === 0 && <div className="p-8 text-center text-[#94A3B8]"><FileText size={32} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No digital signatures recorded yet</p></div>}
      </div>
    </div>
  );
}


// ─── TPI Panel ───────────────────────────────────────────────────────

function TPIPanel({ userRole }) {
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ action: "", target_type: "", target_id: "", reason: "" });

  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/tpi/pending`, { headers: getAuthHeaders() }); setRequests(r.data); } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tpi/request`, form, { headers: getAuthHeaders() });
      toast.success("TPI request created"); setShowForm(false); setForm({ action: "", target_type: "", target_id: "", reason: "" }); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleAction = async (id, action) => {
    try {
      await axios.post(`${API}/tpi/${id}/${action}`, {}, { headers: getAuthHeaders() });
      toast.success(`TPI ${action}d`); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const statColor = { pending: "#D97706", authorized: "#10B981", rejected: "#DC2626" };

  return (
    <div className="space-y-4" data-testid="tpi-panel">
      <div className="bg-[#FFFBEB] border border-[#FDE68A] p-4 text-sm text-[#92400E]">
        Two-Person Integrity requires a <strong>different authorized person</strong> to approve critical actions. The requester cannot authorize their own request.
      </div>
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#64748B]">{requests.length} pending request(s)</p>
        <Button onClick={() => setShowForm(true)} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="create-tpi-btn"><Plus size={14} className="mr-1" />New TPI Request</Button>
      </div>
      <div className="space-y-3">
        {requests.map(r => (
          <div key={r.id} className="bg-white border border-[#E2E8F0] p-5" data-testid={`tpi-${r.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <Users size={18} className="text-[#D97706]" />
                <h3 className="font-bold text-[#0F172A]">{r.action}</h3>
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase" style={{ color: statColor[r.status], backgroundColor: `${statColor[r.status]}10` }}>{r.status}</span>
              </div>
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleAction(r.id, "authorize")} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none text-xs" data-testid={`auth-tpi-${r.id}`}><CheckCircle2 size={12} className="mr-1" />Authorize</Button>
                  <Button size="sm" onClick={() => handleAction(r.id, "reject")} variant="outline" className="rounded-none text-xs border-[#DC2626] text-[#DC2626]" data-testid={`reject-tpi-${r.id}`}>Reject</Button>
                </div>
              )}
            </div>
            <div className="text-xs text-[#64748B] space-y-1">
              <p>Target: <span className="font-mono">{r.target_type}/{r.target_id}</span></p>
              <p>Requested by: <strong>{r.requester_name}</strong> ({r.requester_role}) — {new Date(r.created_at).toLocaleString()}</p>
              {r.reason && <p>Reason: {r.reason}</p>}
              {r.authorizer_name && <p>Authorized by: <strong>{r.authorizer_name}</strong> ({r.authorizer_role}) — {new Date(r.authorized_at).toLocaleString()}</p>}
            </div>
          </div>
        ))}
        {requests.length === 0 && <div className="p-8 text-center text-[#94A3B8]"><Users size={32} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No pending TPI requests</p></div>}
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0]" aria-describedby="tpi-form-desc">
          <DialogHeader><DialogTitle className="font-bold text-[#0F172A]">New TPI Request</DialogTitle></DialogHeader>
          <div id="tpi-form-desc" className="sr-only">Request two-person integrity authorization</div>
          <form onSubmit={handleCreate} className="space-y-4">
            <div><Label>Action *</Label><Input value={form.action} onChange={e => setForm({ ...form, action: e.target.value })} required className="mt-1 rounded-none" placeholder="e.g., Release ITAR document, Override safety interlock" data-testid="tpi-action-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Target Type</Label><select value={form.target_type} onChange={e => setForm({ ...form, target_type: e.target.value })} className="mt-1 w-full h-10 px-3 border border-[#E2E8F0] bg-white text-sm">{["", "blueprint", "work_order", "ncr", "maintenance", "machine", "other"].map(t => <option key={t} value={t}>{t || "Select..."}</option>)}</select></div>
              <div><Label>Target ID</Label><Input value={form.target_id} onChange={e => setForm({ ...form, target_id: e.target.value })} className="mt-1 rounded-none font-mono" /></div>
            </div>
            <div><Label>Reason / Justification</Label><textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="mt-1 w-full h-20 px-3 py-2 border border-[#E2E8F0] text-sm" /></div>
            <Button type="submit" className="w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="submit-tpi-btn">SUBMIT TPI REQUEST</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Print Log Panel ─────────────────────────────────────────────────

function PrintLogPanel({ userRole }) {
  const [entries, setEntries] = useState([]);
  const isAdmin = userRole === "admin" || userRole === "superintendent";

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/print-log`, { headers: getAuthHeaders() }).then(r => setEntries(r.data.entries || [])).catch(() => {});
    }
  }, [isAdmin]);

  if (!isAdmin) return <div className="p-8 text-center text-[#94A3B8]"><p className="text-sm">Print log requires Superintendent or Admin access</p></div>;

  return (
    <div className="space-y-4" data-testid="print-log-panel">
      <p className="text-sm text-[#64748B]">{entries.length} print event(s) recorded. Prints are logged when users use the Print button on blueprints.</p>
      <div className="bg-white border border-[#E2E8F0]">
        <table className="w-full text-sm" data-testid="print-log-table">
          <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
            <tr>{["Timestamp", "User", "Role", "Document", "Copies"].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-bold uppercase">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#F1F5F9]">
            {entries.map(e => (
              <tr key={e.id} className="hover:bg-[#FAFAFA]">
                <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{new Date(e.timestamp).toLocaleString()}</td>
                <td className="px-4 py-3 font-medium text-[#0F172A]">{e.user_name || e.user_id}</td>
                <td className="px-4 py-3 text-xs text-[#64748B] uppercase">{e.user_role}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#0F172A] truncate max-w-[300px]">{e.filename}</td>
                <td className="px-4 py-3 font-bold text-[#0F172A]">{e.copies}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {entries.length === 0 && <div className="p-8 text-center text-[#94A3B8]"><FileText size={32} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No prints logged yet</p></div>}
      </div>
    </div>
  );
}
